import React, { useEffect, useState, useMemo } from 'react';
import { Grid } from '@mui/material';
import ReactHtmlParser from 'react-html-parser';
import { DataGrid } from 'react-data-grid';
import _ from 'lodash';

const workTypes = [
  '0 Poor',
  '1 Poor',
  '2 Poor',
  '3 Poor',
  '4 Needs work',
  '5 Needs work',
  '6 Needs work',
  '7 Good',
  '8 Good',
  '9 Great',
  '10 Great',
];

function ParentTable({
  selectedPlanId,
  planHeaders,
  parentRows,
  onChangeSelId,
}) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    getRows(parentRows);
    // eslint-disable-next-line
  }, [parentRows]);

  const getRows = () => {
    let rowIndex = _.findIndex(planHeaders, { id: selectedPlanId });
    if (rowIndex < 0 || !planHeaders[rowIndex]) {
      setRows([]);
      return;
    }

    const startDate = new Date(planHeaders[rowIndex].StartDate);
    const endDate = new Date(planHeaders[rowIndex].EndDate);
    const interval = planHeaders[rowIndex].Interval;
    const plantype = planHeaders[rowIndex].PlanType;

    if (plantype === 'Milestones') {
      let tmpArr = [];
      parentRows.forEach((row) => {
        let idx = tmpArr.indexOf(row.Description);
        if (idx === -1) {
          tmpArr.push(row.Description);
        }
      });

      let interval_cnt = 0;
      if (interval === 'Years') {
        interval_cnt = endDate.getFullYear() - startDate.getFullYear() + 1;
      } else if (interval === 'Quarters') {
        interval_cnt = Math.round(
          (endDate - startDate) / (1000 * 60 * 60 * 24) / 90,
        );
      } else {
        interval_cnt = Math.round(
          (endDate - startDate) / (1000 * 60 * 60 * 24) / 181,
        );
      }

      let res = [];
      tmpArr.forEach((desc) => {
        let tmp = { Description: desc, Percent: 0 };
        parentRows.forEach((row) => {
          if (row.Description === desc) {
            tmp['Percent'] = row.Percent_complete + tmp['Percent'];
            tmp['OpeningSituation'] = row.OpeningSituation;
            tmp['Employee'] = row.Employee;
          }
        });
        tmp['Percent'] = parseFloat(tmp['Percent'] / interval_cnt).toFixed(2);
        res.push(tmp);
      });

      setRows(res);
    } else {
      setRows(parentRows);
    }
  };

  const handleCellClick = (rowData) => {
    onChangeSelId(rowData.row.id);
  };

  const tableColumns = useMemo(() => {
    let rowIndex = _.findIndex(planHeaders, { id: selectedPlanId });
    if (rowIndex < 0 || !planHeaders[rowIndex]) {
      return [];
    }

    const planType = planHeaders[rowIndex].PlanType;

    let table_columns = [];

    if (planType === 'Objective') {
      table_columns = [
        {
          key: 'Description',
          name: planHeaders[rowIndex].Name,
          renderCell: (props) => (
            <div className="htmlParser">
              {ReactHtmlParser(props.row.Description)}
            </div>
          ),
        },
        {
          key: 'BaseLineRating',
          name: 'Baseline',
          width: 150,
          renderCell: (props) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor:
                  props.row.BaseLineRating > 6
                    ? '#8dc63f'
                    : props.row.BaseLineRating > 3
                    ? '#f39243'
                    : '#de1e1e',
              }}
            >
              {workTypes[props.row.BaseLineRating]}
            </div>
          ),
        },
        {
          key: 'MeasureTarget',
          name: 'Measure | Target',
          renderCell: (props) => (
            <div className="htmlParser">
              {ReactHtmlParser(props.row.MeasureTarget)}
            </div>
          ),
          resizable: true,
        },
        {
          key: 'CurrentRating',
          name: 'Current Status',
          width: 150,
          renderCell: (props) => (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor:
                  props.row.CurrentRating > 6
                    ? '#8dc63f'
                    : props.row.CurrentRating > 3
                    ? '#f39243'
                    : '#de1e1e',
              }}
            >
              {workTypes[props.row.CurrentRating]}
            </div>
          ),
        },
      ];
    } else {
      table_columns = [
        {
          key: 'Employee',
          name: 'Who',
          width: 100,
        },
        {
          key: 'OpeningSituation',
          name: 'Opening Situation',
          renderCell: (props) => (
            <div className="htmlParser">
              {ReactHtmlParser(props.row.OpeningSituation)}
            </div>
          ),
        },
        {
          key: 'Percent',
          name: 'Percent',
          width: 100,
        },
        {
          key: 'Color',
          name: '',
          width: 100,
          renderCell: (props) => {
            if (props.row.Percent > 70) {
              return <div className="div-green"> </div>;
            }
            if (props.row.Percent > 40) {
              return <div className="div-yellow"> </div>;
            }
            return <div className="div-red"> </div>;
          },
        },
      ];
    }

    return table_columns;
  }, [planHeaders, selectedPlanId]);

  return (
    <Grid
      container
      id="parent-grid"
      style={{ height: 214, overflow: 'hidden auto' }}
    >
      <DataGrid
        columns={tableColumns}
        rows={rows}
        onCellClick={handleCellClick}
        rowHeight={(row) => rowHeight(row, tableColumns)}
        minHeight={50 * (rows.length + 2) > 214 ? 50 * (rows.length + 2) : 214}
      />
    </Grid>
  );
}

export default ParentTable;

function getColumnWidth(tableColumns, rowKey, fallbackWidth = 250) {
  const column = tableColumns.find((item) => item.key === rowKey);
  const width = parseInt(column?.width, 10);
  return Number.isNaN(width) ? fallbackWidth : width;
}

function getLineAwareText(value) {
  if (value === null || value === undefined) return '';

  const rawValue = value.toString();
  const lineAwareHtml = rawValue
    .replace(/<br\s*\/?\s*>/gi, '<br />\n')
    .replace(/<\/(p|div|h[1-6]|li|tr)\s*>/gi, '</$1>\n')
    .replace(/<li\b[^>]*>/gi, '• ');

  if (/<\/?[a-z][\s\S]*>/i.test(lineAwareHtml)) {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(lineAwareHtml, 'text/html');
      return doc.body.textContent || '';
    }

    return lineAwareHtml.replace(/<[^>]*>/g, '');
  }

  return lineAwareHtml;
}

function getEstimatedLineCount(value, columnWidth = 250) {
  const text = getLineAwareText(value)
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n|\r/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

  if (!text) return 1;

  const availableWidth = Math.max(columnWidth - 12, 48);
  const charsPerLine = Math.max(Math.floor(availableWidth / 6), 8);

  return text
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .reduce((count, line) => count + Math.max(Math.ceil(line.length / charsPerLine), 1), 0);
}

function rowHeight(row, tableColumns) {
  const minHeight = 50;
  const lineHeight = 17;
  const verticalPadding = 12;

  const maxLineCount = Object.keys(row).reduce((maxLines, rowKey) => {
    const lines = getEstimatedLineCount(
      row[rowKey],
      getColumnWidth(tableColumns, rowKey),
    );
    return Math.max(maxLines, lines);
  }, 1);

  return Math.max(minHeight, maxLineCount * lineHeight + verticalPadding);
}
